import Grid from '@mui/material/Grid';

import Eyebrow from '@/components/Eyebrow/Eyebrow';
import Icon, { IconName } from '@/components/Icon/Icon';
import ResponsiveImage from '@/components/Image/ResponsiveImage';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import {
  LeadForm,
  LeadFormStyle,
} from '@/components/TopLevelComponents/LeadForm/LeadForm';
import type { FrontifyImageContent } from '@/contentful/contentful-exports';
import { LeadFormType } from '@/enums/FormType';
import useViewPortSize from '@/hooks/useViewportSize';
import useGlobalStyles from '@/styles/global.styles';
import { brandColors } from '@/styles/theme/values/brandColors';
import type { HeroLeadFormCF, LeadCreatedModalCF } from '@/types';
import { getLeadFormDialogContent } from '@/utils/leadForm';

import useStyles from './HeroLeadForm.styles';

type HeroLeadFormImage = FrontifyImageContent & { altText?: string };

interface HeroLeadFormProps {
  content: HeroLeadFormCF;
}

export default function HeroLeadForm({
  content: {
    fields: {
      eyebrow,
      hasStars = false,
      headline,
      subhead,
      images: imagesField,
      formHeading,
      ownerQuestion,
      submitButtonText,
      tcpa,
      leadCreatedModal,
      leadNotInServiceModal,
      campaignId,
      overrideCampaignId = false,
      immersive = false,
    },
  },
}: HeroLeadFormProps) {
  const images = (imagesField ?? []) as unknown as HeroLeadFormImage[];

  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const { classes, cx } = useStyles();
  const { isSmall, isExtraSmall } = useViewPortSize();
  const isMdOrLarger = !isExtraSmall && !isSmall;

  const successDialogContent = getLeadFormDialogContent(
    leadCreatedModal as LeadCreatedModalCF,
    isMdOrLarger
  );
  const failDialogContent = getLeadFormDialogContent(
    leadNotInServiceModal as LeadCreatedModalCF,
    isMdOrLarger
  );

  return (
    <SpacingContainer className={classes.container} isFeatured>
      <GridContainer
        gridProps={{ className: immersive ? classes.immersive : undefined }}
      >
        {/* Text Content */}
        <Grid
          className={classes.textContentContainer}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <div className={classes.textContent}>
            {eyebrow && (
              <div className={cx({ [classes.eyebrowWithStars]: hasStars })}>
                {hasStars && (
                  <div className={classes.stars}>
                    <Icon
                      name={IconName.STAR}
                      width={24}
                      color={brandColors.mediumBlue}
                    />
                    <Icon
                      name={IconName.STAR}
                      width={24}
                      color={brandColors.mediumBlue}
                    />
                    <Icon
                      name={IconName.STAR}
                      width={24}
                      color={brandColors.mediumBlue}
                    />
                    <Icon
                      name={IconName.STAR}
                      width={24}
                      color={brandColors.mediumBlue}
                    />
                    <Icon
                      name={IconName.STAR}
                      width={24}
                      color={brandColors.mediumBlue}
                    />
                  </div>
                )}
                <Eyebrow
                  text={eyebrow}
                  isOnDark={false}
                  className={cx(globalClasses.bodyLg, classes.eyebrown)}
                />
              </div>
            )}
            {headline && (
              <h1 className={cx(globalClasses.h1, classes.headline)}>
                {headline}
              </h1>
            )}
            {subhead && (
              <p className={cx(globalClasses.bodyLg, classes.subheading)}>
                {subhead}
              </p>
            )}
          </div>
          {/* Image Row */}
          <div className={classes.imagesContainer}>
            {images.map((image, index) => {
              if (!image || typeof image !== 'object') {
                console.warn(`Skipping invalid image at index ${index}`, image);
                return null;
              }

              return (
                <ResponsiveImage
                  key={image.id}
                  src={image}
                  alt={image.altText || image.title || ''}
                  sizes={{ xs: 68, sm: 165, md: 121, lg: 133, xl: 180 }}
                  className={classes.image}
                />
              );
            })}
          </div>
        </Grid>

        {/* Lead Form */}
        <Grid
          className={classes.leadFormContainer}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <div className={classes.leadForm}>
            <LeadForm
              heading={formHeading}
              ownerQuestion={ownerQuestion}
              submitButtonText={submitButtonText}
              tcpa={tcpa}
              successDialogContent={successDialogContent}
              failDialogContent={failDialogContent}
              campaignId={campaignId}
              formType={LeadFormType.ZEUS}
              overrideCampaignId={overrideCampaignId}
              leadFormstyle={LeadFormStyle.HERO}
            />
          </div>
        </Grid>
      </GridContainer>
    </SpacingContainer>
  );
}
